import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useApp } from '../store';
import { can } from '../permissions';
import { rollupBudget } from '../projects';
import { fmtMoney, currencyByCode } from '../money';
import BudgetLedger from './BudgetLedger';
import InvoiceDrawer from './InvoiceDrawer';
import Icon from './Icon';

/*
 * BUDGET & PAYMENTS — this project's money on one screen (P9, 3 Oct 2026).
 *
 * The per-project budget used to live in a modal off the Plan (BudgetModal →
 * BudgetLedger), which capped its width and kept the budget and the project's
 * payments in two places. It is promoted here into a real screen, nested under
 * Plan: the same budget health, forecast-vs-the-ledger, capex/opex split, the
 * payments LEDGER and the governance positions, read as one surface — with a
 * quiet connector onward to the cross-board "Authorised to pay" queue (R15).
 *
 * It REUSES BudgetLedger and InvoiceDrawer (the design-lead's own build note):
 * the ledger renders inline full-width, and opening a line slides the invoice
 * drawer in over a scrim, exactly as the modal did.
 */

function reducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  );
}
const DRAWER_MS = 260;

export default function BudgetPay() {
  const projectId = useApp((s) => s.projectId);
  const project = useApp((s) => s.projects.find((p) => p.id === projectId));
  const role = useApp((s) => s.shownRole);
  const mayPlan = can(role, 'editSettings');
  const base = useApp((s) => s.currency);
  const fxRates = useApp((s) => s.fxRates);
  // A deep-link from the Plan ("Set budget" / "Add a forecast") asks the screen to
  // open straight into the edit form. Consumed once on arrival, then cleared.
  const budgetEnterEdit = useApp((s) => s.budgetEnterEdit);
  const setBudgetEnterEdit = useApp((s) => s.setBudgetEnterEdit);

  // The budget verdict for the header pill — the same figure the band shows,
  // read as a RAG: on track while something is left, over once it is not.
  const rollup = useMemo(
    () => (project ? rollupBudget(project, { base, rates: fxRates }) : null),
    [project, base, fxRates],
  );

  // The open ledger line — the invoice drawer, driven exactly as the old modal
  // drove it so the slide-in and the single-Escape-closes-the-drawer behaviour
  // are unchanged.
  const [openLineId, setOpenLineId] = useState<string | null>(null);
  const [drawerLeaving, setDrawerLeaving] = useState(false);
  const drawerTimer = useRef<ReturnType<typeof setTimeout>>();
  useEffect(() => () => clearTimeout(drawerTimer.current), []);

  // The header "Edit budget" opens the band's form through this signal (the band
  // hides its own inline Edit button on this screen, so the control is not twice).
  const [editSignal, setEditSignal] = useState(0);
  // Arriving from a Plan edit deep-link: open the form once, then clear the flag so
  // a later plain visit stays in view mode.
  useEffect(() => {
    if (budgetEnterEdit) {
      if (mayPlan) setEditSignal((s) => s + 1);
      setBudgetEnterEdit(false);
    }
    // once, on arrival
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openLine = useCallback((id: string) => {
    clearTimeout(drawerTimer.current);
    drawerTimer.current = undefined;
    setDrawerLeaving(false);
    setOpenLineId(id);
  }, []);
  const closeDrawer = useCallback(() => {
    if (reducedMotion()) {
      setOpenLineId(null);
      return;
    }
    if (drawerTimer.current) return;
    setDrawerLeaving(true);
    drawerTimer.current = setTimeout(() => {
      drawerTimer.current = undefined;
      setDrawerLeaving(false);
      setOpenLineId(null);
    }, DRAWER_MS);
  }, []);

  if (!project) return null;

  const openLine_ = openLineId ? (project.invoices ?? []).find((l) => l.id === openLineId) : undefined;
  const showDrawer = !!openLine_;

  // The verdict pill. No budget set yet → nothing to judge, so no pill.
  const verdict = (() => {
    if (!rollup || rollup.budget == null || rollup.remaining == null) return null;
    if (rollup.remaining < 0) return { tone: 'r', text: `Over by ${fmtMoney(Math.abs(rollup.remaining))}` };
    return { tone: 'g', text: `On track · ${fmtMoney(rollup.remaining)} left` };
  })();

  const cur = currencyByCode(base);

  return (
    <div className="budgetpay cap-fill">
      <div className="fd-head">
        <div className="fd-head-main">
          <h2 className="fd-title">Budget &amp; Payments</h2>
          <p className="fd-sub">
            {project.name} · in {cur.symbol} {cur.code} (base)
          </p>
        </div>
        <div className="fd-head-side">
          {verdict && (
            <span className={`rag ${verdict.tone}`}>
              <span className="dot" />
              {verdict.text}
            </span>
          )}
          {mayPlan && (
            <button className="btn btn-small" data-reveal="contract-value" onClick={() => setEditSignal((s) => s + 1)} title="Change the approved budget, split or forecast">
              <Icon name="pencil" size={13} /> Edit budget
            </button>
          )}
        </div>
      </div>

      <BudgetLedger
        openLineId={openLineId}
        onOpenLine={openLine}
        hideInlineEdit
        editSignal={editSignal}
      />

      {showDrawer && openLine_ && (
        <div className="overlay budgetpay-scene" onClick={() => closeDrawer()}>
          <InvoiceDrawer projectId={project.id} line={openLine_} leaving={drawerLeaving} onClose={closeDrawer} />
        </div>
      )}
    </div>
  );
}
